import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  Account,
  Asset,
  FiatDepositInstructionControllerService,
  FiatDepositInstructionCreateRequest,
  FiatDepositInstructionDto,
  SettlementControllerService,
  SettlementDestinationDto,
} from '@api/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStoreFeature, type, withMethods, withProps, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { ConfirmationDialogService } from '@shared/services/confirmation-dialog.service';
import { NotificationService } from '@shared/services/notifications/notifications.service';
import { DepositInstructionDialogComponent } from 'app/settings/components/deposit-instructions/deposit-instruction-dialog/deposit-instruction-dialog.component';
import { pipe, switchMap, take } from 'rxjs';

export interface DepositInstructionViewModel extends FiatDepositInstructionDto {
  asset: Asset | undefined;
}

export const useTransferInstructions = () =>
  signalStoreFeature(
    {
      props: {
        _notificationService: type<NotificationService>(),
      },
    },
    withState({
      withdrawAddressInfo: new Array<SettlementDestinationDto>(),
      depositInstructions: new Array<FiatDepositInstructionDto>(),
    }),
    withProps(() => ({
      _settlementService: inject(SettlementControllerService),
      _depositInstructionsService: inject(FiatDepositInstructionControllerService),
      _dialogService: inject(MatDialog),
      _confirmationDialogService: inject(ConfirmationDialogService),
    })),
    withMethods(store => {
      const loadWithdrawAddressInfo = rxMethod<{
        assetId: number;
        accountId: number;
      }>(
        pipe(
          switchMap(({ accountId, assetId }) =>
            store._settlementService.getSettlementDestinations({ accountId, assetId }).pipe(
              tapResponse({
                next: withdrawAddressInfo => patchState(store, { withdrawAddressInfo }),
                error: (error: HttpErrorResponse) =>
                  store._notificationService.showErrorNotification('Failed to load withdraw address info', error),
              }),
            ),
          ),
        ),
      );

      const loadDepositInstructions = rxMethod<{
        businessEntity?: Account.BusinessEntityEnum;
      }>(
        pipe(
          switchMap(({ businessEntity }) =>
            store._depositInstructionsService.getFiatDepositInstructions({ businessEntity }).pipe(
              tapResponse({
                next: depositInstructions => patchState(store, { depositInstructions }),
                error: (error: HttpErrorResponse) =>
                  store._notificationService.showErrorNotification(
                    `The deposit instructions couldn't be loaded`,
                    error,
                  ),
              }),
            ),
          ),
        ),
      );

      const createDepositInstruction = (request: FiatDepositInstructionCreateRequest) => {
        store._depositInstructionsService
          .createFiatDepositInstruction({ fiatDepositInstructionCreateRequest: request })
          .pipe(take(1))
          .subscribe({
            next: () => {
              store._notificationService.showSuccessNotification(
                'The deposit instruction has been created successfully',
              );
              store._dialogService.closeAll();
              loadDepositInstructions({});
            },
            error: (error: HttpErrorResponse) =>
              store._notificationService.showErrorNotification(`The deposit instruction couldn't be created`, error),
          });
      };

      const updateDepositInstruction = (request: FiatDepositInstructionDto) => {
        store._depositInstructionsService
          .updateFiatDepositInstruction({ fiatDepositInstructionUpdateRequest: request })
          .pipe(take(1))
          .subscribe({
            next: () => {
              store._notificationService.showSuccessNotification(
                'The deposit instruction has been updated successfully',
              );
              store._dialogService.closeAll();
              loadDepositInstructions({});
            },
            error: (error: HttpErrorResponse) =>
              store._notificationService.showErrorNotification(`The deposit instruction couldn't be updated`, error),
          });
      };

      const openDepositInstructionDialog = (depositInstruction?: FiatDepositInstructionDto) => {
        store._dialogService.open(DepositInstructionDialogComponent, {
          data: depositInstruction,
        });
      };

      const openRemoveDepositInstructionDialog = (depositInstruction: DepositInstructionViewModel) => {
        store._confirmationDialogService
          .confirm({
            title: 'Remove deposit instruction',
            message: `Are you sure you want to remove the deposit instruction ${depositInstruction.name} ${depositInstruction.asset?.code} ?`,
            confirmText: 'Remove',
          })
          .pipe(take(1))
          .subscribe(() => {
            removeDepositInstruction(depositInstruction.id);
          });
      };

      const removeDepositInstruction = (instructionId: number) => {
        store._depositInstructionsService
          .deleteFiatDepositInstruction({ instructionId })
          .pipe(take(1))
          .subscribe({
            next: () => {
              store._notificationService.showSuccessNotification(
                'The deposit instruction has been removed successfully',
              );
              loadDepositInstructions({});
            },
            error: (error: HttpErrorResponse) =>
              store._notificationService.showErrorNotification(`The deposit instruction couldn't be removed`, error),
          });
      };

      return {
        loadWithdrawAddressInfo,
        loadDepositInstructions,
        createDepositInstruction,
        updateDepositInstruction,
        openDepositInstructionDialog,
        openRemoveDepositInstructionDialog,
      };
    }),
  );
